/**
 * Curator Mode client helpers.
 * Token is session-only. Password is NEVER stored or bundled — only typed into the login form.
 */
import { api } from './api';

const TOKEN_KEY = 'wavekit.curator.token';
const EXPIRES_KEY = 'wavekit.curator.expiresAt';

export const getStoredCuratorToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

export const getStoredCuratorExpiresAt = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(EXPIRES_KEY) || null;
  } catch {
    return null;
  }
};

export const storeCuratorSession = (token, expiresAt) => {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      window.sessionStorage.setItem(TOKEN_KEY, token);
      if (expiresAt) window.sessionStorage.setItem(EXPIRES_KEY, expiresAt);
    } else {
      window.sessionStorage.removeItem(TOKEN_KEY);
      window.sessionStorage.removeItem(EXPIRES_KEY);
    }
  } catch {
    // private mode / quota
  }
};

export const clearCuratorSession = () => storeCuratorSession(null);

export const isStoredSessionExpired = () => {
  const exp = getStoredCuratorExpiresAt();
  if (!exp) return false;
  const ms = Date.parse(exp);
  if (!Number.isFinite(ms)) return false;
  return Date.now() >= ms;
};

const authHeaders = (token) => {
  const t = token || getStoredCuratorToken();
  if (!t) return {};
  return { Authorization: `Bearer ${t}` };
};

export const curatorLogin = async (password) => {
  const { data } = await api.post('/curator/login', { password });
  if (data?.token) {
    storeCuratorSession(data.token, data.expires_at);
  }
  return data;
};

export const curatorLogout = async () => {
  try {
    await api.post('/curator/logout', {}, { headers: authHeaders() });
  } catch {
    // still clear local session
  }
  clearCuratorSession();
  return { ok: true, curator: false };
};

export const curatorSession = async () => {
  const token = getStoredCuratorToken();
  if (!token || isStoredSessionExpired()) {
    if (token && isStoredSessionExpired()) clearCuratorSession();
    return { curator: false };
  }
  try {
    const { data } = await api.get('/curator/session', { headers: authHeaders(token) });
    if (!data?.curator) clearCuratorSession();
    return data;
  } catch {
    clearCuratorSession();
    return { curator: false };
  }
};

export const setCuratorLanding = async ({ projectId, analysisId, chartView }) => {
  const token = getStoredCuratorToken();
  if (!token) {
    const err = new Error('Not curator');
    err.status = 401;
    throw err;
  }
  const { data } = await api.post(
    '/curator/landing',
    {
      project_id: projectId,
      analysis_id: analysisId,
      chart_view: chartView,
    },
    { headers: authHeaders(token) },
  );
  return data;
};

export const getCuratorLanding = async () => {
  const { data } = await api.get('/curator/landing');
  return data;
};
